"use client";

import {
  Fragment,
  createElement,
  useCallback,
  useState,
  type ReactElement,
} from "react";
import {
  PlanReadOnlyDialog,
  type PlanReadOnlyKind,
} from "@/components/freemium/PlanReadOnlyDialog";
import { ProUpgradeLimitDialog } from "@/components/freemium/ProUpgradeLimitDialog";
import { useCurrentUser } from "@/hooks/queries/useCurrentUser";
import {
  errorCodeFromUnknown,
  resolveWriteBlockKind,
} from "@/lib/plan-access-client";
import {
  planReadOnlyKindFromErrorCode,
  proUpgradeVariantFromErrorCode,
} from "@/lib/plan-access-errors";
import type { ProUpgradeLimitVariant } from "@/lib/plan-limit-ui";

export function useProUpgradeLimitDialog() {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [variant, setVariant] = useState<ProUpgradeLimitVariant | null>(null);
  const [readOnlyOpen, setReadOnlyOpen] = useState(false);
  const [readOnlyKind, setReadOnlyKind] = useState<PlanReadOnlyKind | null>(
    null,
  );
  const { data: user } = useCurrentUser();
  const isPro = Boolean(user?.is_pro);

  const showProLimit = useCallback((next: ProUpgradeLimitVariant) => {
    setVariant(next);
    setUpgradeOpen(true);
  }, []);

  const showReadOnly = useCallback((kind: PlanReadOnlyKind) => {
    setReadOnlyKind(kind);
    setReadOnlyOpen(true);
  }, []);

  const showFromErrorCode = useCallback(
    (code: string | undefined): boolean => {
      const readOnly = planReadOnlyKindFromErrorCode(code);
      if (readOnly) {
        showReadOnly(readOnly);
        return true;
      }
      const mapped = proUpgradeVariantFromErrorCode(code);
      if (mapped) {
        showProLimit(mapped);
        return true;
      }
      return false;
    },
    [showProLimit, showReadOnly],
  );

  const showProLimitFromCode = useCallback(
    (code: string | undefined, fallback: ProUpgradeLimitVariant = "folders") => {
      if (showFromErrorCode(code)) return;
      showProLimit(fallback);
    },
    [showFromErrorCode, showProLimit],
  );

  const handlePlanAccessError = useCallback(
    (err: unknown): boolean => showFromErrorCode(errorCodeFromUnknown(err)),
    [showFromErrorCode],
  );

  const guardWriteAccess = useCallback(
    (input: Parameters<typeof resolveWriteBlockKind>[0]): boolean => {
      const kind = resolveWriteBlockKind(input);
      if (!kind) return true;
      showReadOnly(kind);
      return false;
    },
    [showReadOnly],
  );

  const proLimitDialog: ReactElement = createElement(
    Fragment,
    null,
    createElement(PlanReadOnlyDialog, {
      open: readOnlyOpen && readOnlyKind !== null,
      kind: readOnlyKind,
      onOpenChange: (next: boolean) => {
        setReadOnlyOpen(next);
        if (!next) setReadOnlyKind(null);
      },
    }),
    createElement(ProUpgradeLimitDialog, {
      open: upgradeOpen && variant !== null,
      variant,
      isPro,
      onOpenChange: (next: boolean) => {
        setUpgradeOpen(next);
        if (!next) setVariant(null);
      },
    }),
  );

  return {
    showProLimit,
    showProLimitFromCode,
    showReadOnly,
    showFromErrorCode,
    handlePlanAccessError,
    guardWriteAccess,
    proLimitDialog,
  };
}
